import React, { useState, useCallback } from 'react';
import texasApi from '../../services/texasApi';
import { LeadPaintStatus } from '../../services/texasApi';

export const LeadPaintDisclosure: React.FC = () => {
  const [propertyId, setPropertyId] = useState<string>('');
  const [status, setStatus] = useState<LeadPaintStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [pamphletText, setPamphletText] = useState('');

  // The pamphlet and signature endpoints act on an existing record and return
  // 404 ("No disclosure record for this property") when none has been started.
  // Gate those two actions on a loaded, started record so a raw 404 is never the
  // first feedback the user gets for a normal "not started yet" state.
  const recordExists = !!status && (status as { status?: string }).status !== 'NOT_STARTED';

  const fetchStatus = useCallback(async (pid: string) => {
    if (!pid) return;
    setLoading(true);
    setError(null);
    try {
      const data = await texasApi.leadPaint.getProperty(pid);
      setStatus(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load disclosure status');
    } finally {
      setLoading(false);
    }
  }, []);

  const handlePamphlet = async () => {
    if (!propertyId.trim()) return;
    if (!recordExists) {
      setError('Start the disclosure record first, then record the pamphlet.');
      return;
    }
    setRecording(true);
    try {
      await texasApi.leadPaint.recordPamphlet(propertyId.trim(), { text: pamphletText });
      setPamphletText('');
      await fetchStatus(propertyId.trim());
    } catch (err: any) {
      setError(err?.message || 'Pamphlet recording failed');
    } finally {
      setRecording(false);
    }
  };

  const handleSignDisclosure = async () => {
    if (!propertyId.trim()) return;
    if (!recordExists) {
      setError('Start the disclosure record first, then sign it.');
      return;
    }
    setRecording(true);
    try {
      await texasApi.leadPaint.signDisclosure(propertyId.trim(), {});
      await fetchStatus(propertyId.trim());
    } catch (err: any) {
      setError(err?.message || 'Disclosure signing failed');
    } finally {
      setRecording(false);
    }
  };

  const handleCreateOrUpdate = async () => {
    if (!propertyId.trim()) return;
    setRecording(true);
    try {
      await texasApi.leadPaint.createOrUpdate(propertyId.trim(), { builtBefore1978: true });
      await fetchStatus(propertyId.trim());
    } catch (err: any) {
      setError(err?.message || 'Record creation failed');
    } finally {
      setRecording(false);
    }
  };

  return (
    <div data-testid="lead-paint-disclosure" className="texas-page-container">
      <h1>EPA Lead-Based Paint Disclosure</h1>
      <p className="page-description">
        Manage EPA lead-paint disclosure per Texas Property Code and federal requirements.
      </p>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Property ID"
          value={propertyId}
          onChange={(e) => setPropertyId(e.target.value)}
          data-testid="property-id-input"
        />
        <button onClick={() => fetchStatus(propertyId.trim())} disabled={loading} data-testid="load-btn">
          {loading ? 'Loading...' : 'Load Status'}
        </button>
      </div>

      {error && <div className="error" data-testid="error">{error}</div>}

      {status && (
        <div className="disclosure-section" data-testid="disclosure-status">
          <h2>Property: {status.propertyId}</h2>
          <table className="status-table">
            <tbody>
              <tr data-testid="built-before-1978">
                <td>Built Before 1978</td>
                <td>{status.builtBefore1978 ? 'Yes' : 'No'}</td>
              </tr>
              <tr data-testid="pamphlet-acknowledged">
                <td>Pamphlet Acknowledged</td>
                <td>{status.pamphletAcknowledged ? '✅ Yes' : '❌ No'}</td>
              </tr>
              <tr data-testid="disclosure-signed">
                <td>Disclosure Signed</td>
                <td>{status.disclosureSigned ? '✅ Yes' : '❌ No'}</td>
              </tr>
              {status.blocker && (
                <tr data-testid="blocker">
                  <td>Blocker</td>
                  <td className="blocker-text">{status.blocker}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="action-bar">
        <button onClick={handleCreateOrUpdate} disabled={recording || !propertyId.trim()} data-testid="create-btn">
          {recording ? 'Saving...' : 'Create/Update Record'}
        </button>
        <button
          onClick={handlePamphlet}
          disabled={recording || !propertyId.trim() || !recordExists}
          title={recordExists ? undefined : 'Start the disclosure record first'}
          data-testid="pamphlet-btn"
        >
          {recording ? 'Recording...' : 'Record Pamphlet'}
        </button>
        <button
          onClick={handleSignDisclosure}
          disabled={recording || !propertyId.trim() || !recordExists}
          title={recordExists ? undefined : 'Start the disclosure record first'}
          data-testid="sign-btn"
        >
          {recording ? 'Signing...' : 'Sign Disclosure'}
        </button>
      </div>

      <div className="pamphlet-section">
        <h3>EPA Pamphlet Text</h3>
        <textarea
          placeholder="Enter pamphlet text"
          value={pamphletText}
          onChange={(e) => setPamphletText(e.target.value)}
          data-testid="pamphlet-text"
          rows={4}
        />
      </div>
    </div>
  );
};

export default LeadPaintDisclosure;
